import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Clock, CheckCircle, XCircle, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';

const OUTCOME_MAP = {
  order: { label: 'طلب', icon: ShoppingCart, color: 'text-emerald-600 bg-emerald-50' },
  collection: { label: 'تحصيل', icon: CheckCircle, color: 'text-blue-600 bg-blue-50' },
  info_only: { label: 'متابعة', icon: Clock, color: 'text-amber-600 bg-amber-50' },
  no_sale: { label: 'بدون نتيجة', icon: XCircle, color: 'text-gray-500 bg-gray-100' },
};

export default function VisitsTab({ visits, customerId, customerName }) {
  const navigate = useNavigate();

  const lastVisit = visits?.[0];
  const daysSinceVisit = lastVisit
    ? Math.floor((Date.now() - new Date(lastVisit.visit_date)) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-card border border-border rounded-2xl p-3 text-center">
          <p className="text-2xl font-bold text-primary">{visits.length}</p>
          <p className="text-xs text-muted-foreground">إجمالي الزيارات</p>
        </div>
        <div className={`rounded-2xl p-3 text-center border ${daysSinceVisit > 14 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <p className={`text-lg font-bold ${daysSinceVisit > 14 ? 'text-amber-700' : 'text-emerald-700'}`}>
            {daysSinceVisit !== null ? `${daysSinceVisit} يوم` : '—'}
          </p>
          <p className="text-xs text-muted-foreground">منذ آخر زيارة</p>
        </div>
      </div>

      {/* New visit button */}
      <Button
        onClick={() => navigate(`/visits/new?customer_id=${customerId}&customer_name=${encodeURIComponent(customerName)}`)}
        className="w-full gap-2" variant="outline"
      >
        <Plus className="w-4 h-4" /> تسجيل زيارة
      </Button>

      {/* Visit timeline */}
      {visits.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="w-14 h-14 bg-muted rounded-2xl flex items-center justify-center">
            <MapPin className="w-7 h-7 text-muted-foreground" />
          </div>
          <p className="font-semibold text-foreground">لا توجد زيارات</p>
          <p className="text-sm text-muted-foreground">سجّل أول زيارة لهذا العميل</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute right-[27px] top-0 bottom-0 w-0.5 bg-border" />
          <div className="space-y-3">
            {visits.map(visit => {
              const outcome = OUTCOME_MAP[visit.outcome] || OUTCOME_MAP.no_sale;
              const Icon = outcome.icon;
              const date = new Date(visit.visit_date);
              const days = Math.floor((Date.now() - date) / (1000 * 60 * 60 * 24));
              return (
                <div key={visit.id} className="flex gap-3">
                  <div className={`w-[38px] h-[38px] rounded-xl flex items-center justify-center flex-shrink-0 z-10 ${outcome.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 bg-card border border-border rounded-2xl p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{outcome.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {date.toLocaleDateString('ar-EG', { day: 'numeric', month: 'long', year: 'numeric' })}
                          {' · '}
                          {days === 0 ? 'اليوم' : days === 1 ? 'أمس' : `منذ ${days} يوم`}
                        </p>
                        {visit.salesman_name && (
                          <p className="text-xs text-muted-foreground">بواسطة: {visit.salesman_name}</p>
                        )}
                      </div>
                    </div>
                    {visit.notes && (
                      <p className="text-xs text-foreground mt-2 bg-muted/50 rounded-lg p-2">{visit.notes}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}